import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["open", "position", "message", "state", "onDismiss", "showCloseBtn", "dismissOnClickOutside", "dismissOnDelay", "unmountChildrenOnClose", "isDark", "dark", "className", "children", "datatestid", "htmlProps"];
import { Fragment, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import styled from 'styled-components';
import CloseSmall from '@crehana/icons/outline/CloseSmall';
import { useMountChildrenValidation } from '@crehana/react-hooks';
import IconButton from "../Buttons/IconButton";
import { baseLighter } from "../styles/colors";
import { stateIconColors, stateIcons } from "../styles/states";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Snack = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-k3c66v-0"
})(["\n  max-width: 468px;\n  top: 0;\n  left: 16px;\n  right: 16px;\n  &.snack-is-visible {\n    visibility: visible;\n    opacity: 1;\n    transform: translateY(16px);\n    transition: opacity 150ms linear, transform 150ms linear;\n  }\n  &.snack-is-hidden {\n    visibility: hidden;\n    opacity: 0;\n    transform: translateY(0);\n    transition: visibility 0s 150ms, opacity 150ms linear,\n      transform 150ms linear;\n  }\n  ", " {\n    ", "\n  }\n"], function (_ref) {
  var theme = _ref.theme;
  return theme.breakpoints.up('sm');
}, function (_ref2) {
  var position = _ref2.position;
  if (position === 'top-center') {
    return "\n          right: unset;\n          left: 50%;\n          &.snack-is-visible {\n            visibility: visible;\n            opacity: 1;\n            transform: translate(-50%, 48px);\n            transition: opacity 150ms linear, transform 150ms linear;\n          }\n          &.snack-is-hidden {\n            visibility: hidden;\n            opacity: 0;\n            transform: translate(-50%, 0);\n            transition: visibility 0s 150ms, opacity 150ms linear,\n              transform 150ms linear;\n          }\n        ";
  }
  return "\n        ".concat(position === 'bottom-right' ? 'bottom: 48px; top: unset;' : 'top: 48px;', "\n        left: unset;\n        right: 0;\n        &.snack-is-visible {\n          visibility: visible;\n          opacity: 1;\n          transform: translateX(-48px);\n          transition: opacity 150ms linear, transform 150ms linear;\n        }\n        &.snack-is-hidden {\n          visibility: hidden;\n          opacity: 0;\n          transform: translateX(0);\n          transition: visibility 0s 150ms, opacity 150ms linear,\n            transform 150ms linear;\n        }\n      ");
});
var Snackbar = function Snackbar(_ref3) {
  var _IconComponent, _p;
  var open = _ref3.open,
    _ref3$position = _ref3.position,
    position = _ref3$position === void 0 ? 'bottom-right' : _ref3$position,
    message = _ref3.message,
    state = _ref3.state,
    onDismiss = _ref3.onDismiss,
    showCloseBtn = _ref3.showCloseBtn,
    dismissOnClickOutside = _ref3.dismissOnClickOutside,
    dismissOnDelay = _ref3.dismissOnDelay,
    unmountChildrenOnClose = _ref3.unmountChildrenOnClose,
    isDark = _ref3.isDark,
    dark = _ref3.dark,
    className = _ref3.className,
    children = _ref3.children,
    datatestid = _ref3.datatestid,
    htmlProps = _ref3.htmlProps,
    snackBarProps = _objectWithoutProperties(_ref3, _excluded);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isClient = _useState2[0],
    setIsClient = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    delayTimeout = _useState4[0],
    setDelayTimeout = _useState4[1];
  var shouldMountChildren = useMountChildrenValidation({
    isOpen: open,
    unmountChildrenOnClose: unmountChildrenOnClose
  });
  var snackbarRef = useRef(null);
  var IconComponent = state ? stateIcons[state] : undefined;
  var iconColor = state ? stateIconColors[state] : undefined;
  useEffect(function () {
    setIsClient(true);
  }, []);
  useEffect(function () {
    // Handle dismiss timeout if necessary
    if (dismissOnDelay) {
      if (delayTimeout) {
        clearTimeout(delayTimeout);
      }
      if (open) {
        setDelayTimeout(window.setTimeout(function () {
          if (onDismiss) {
            onDismiss();
          }
        }, dismissOnDelay));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  var onClose = function onClose(e) {
    e.preventDefault();
    if (open && onDismiss) {
      onDismiss();
    }
  };
  useEffect(function () {
    var listener = function listener(event) {
      if (snackbarRef.current && !snackbarRef.current.contains(event.target) && onDismiss) {
        onDismiss();
      }
    };
    if (dismissOnClickOutside) {
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);
    } else {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    }
    return function () {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [dismissOnClickOutside, onDismiss]);
  var renderSnackbarContent = function renderSnackbarContent() {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [IconComponent && iconColor && (_IconComponent || (_IconComponent = /*#__PURE__*/_jsx(IconComponent, {
        color: iconColor,
        size: 24,
        className: "mr-16"
      }))), message ? _p || (_p = /*#__PURE__*/_jsx("p", {
        className: "font-body4",
        children: message
      })) : children]
    });
  };
  if (isClient) {
    return /*#__PURE__*/ReactDOM.createPortal(
    /*#__PURE__*/
    // @ts-ignore
    _jsxs(Snack, _objectSpread(_objectSpread(_objectSpread({
      ref: snackbarRef,
      className: cn('fixed z-snackbar rounded-5 shadow-snackbar-mobile sm:shadow-snackbar', isDark || dark ? 'bg-base-dark text-white' : 'bg-white text-base-main', open ? 'snack-is-visible' : 'snack-is-hidden', {
        'flex items-center': !className && !!state,
        'pr-64': !className && !!state && showCloseBtn
      }, className || 'py-16 px-24'),
      position: position,
      "data-testid": datatestid
    }, snackBarProps), htmlProps), {}, {
      children: [shouldMountChildren && renderSnackbarContent(), showCloseBtn && /*#__PURE__*/_jsx(IconButton, {
        className: "absolute top-0 right-0 mt-8 mr-8 cursor-pointer sm:mr-16 sm:mt-16",
        xs: true,
        onClick: onClose,
        customIcon: CloseSmall,
        customColor: baseLighter,
        customSize: 24
      })]
    })), document.body);
  }
  return null;
};
export default Snackbar;